import React, { useState } from 'react';
import { Modal, Button, Alert, Form, Row, Col } from 'react-bootstrap';
import { FaFilePdf, FaDownload, FaCloudUploadAlt } from 'react-icons/fa';
import axiosInstance from 'helpers/api';

interface SimplePDFEditorProps {
  show: boolean;
  onHide: () => void;
  documentUrl: string;
  documentName: string;
  onComplete?: () => void;
}

const SimplePDFEditor: React.FC<SimplePDFEditorProps> = ({
  show,
  onHide,
  documentUrl,
  documentName,
  onComplete
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [approach, setApproach] = useState<'docusign' | 'simple' | 'external'>('simple');

  const handleSimpleConversion = async () => {
    try {
      setLoading(true);
      setError(null);

      // For simple conversion, we'll use a different approach
      // Option 1: Use react-pdf-viewer with annotation capabilities
      window.open(`https://www.ilovepdf.com/pdf_to_editable_pdf?url=${encodeURIComponent(documentUrl)}`, '_blank');
      
      setSuccess(true);
      setTimeout(() => {
        onHide();
        if (onComplete) onComplete();
      }, 2000);

    } catch (err) {
      console.error('Error:', err);
      setError('Failed to process PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleDocuSignStyle = () => {
    // Redirect to DocuSign-style editor
    const docuSignUrl = `https://app.docusign.com/send/documents/prepare?file=${encodeURIComponent(documentUrl)}`;
    window.open(docuSignUrl, '_blank');
    
    setSuccess(true);
    setTimeout(() => {
      onHide();
      if (onComplete) onComplete();
    }, 2000);
  };

  const handleExternalService = async () => {
    setLoading(true);
    setError(null);

    try {
      // Use Adobe's free online tool
      window.open('https://www.adobe.com/acrobat/online/pdf-to-fillable-form.html', '_blank');
      
      // Show instructions
      setError('Upload your PDF to Adobe\'s tool in the new tab. It\'s free and works with complex PDFs.');
      
    } catch (err) {
      setError('Failed to open external service');
    } finally {
      setLoading(false);
    }
  };

  const downloadAndUploadElsewhere = async () => {
    try {
      // Download the PDF
      const response = await fetch(documentUrl);
      const blob = await response.blob();
      
      // Create download link
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = documentName;
      a.click();
      URL.revokeObjectURL(downloadUrl);
      
      // Show upload instructions
      setError(`PDF downloaded! Now you can:
1. Upload to Adobe Acrobat online (free)
2. Use DocuSign's prepare feature
3. Use HelloSign's template builder
4. Use PDFescape online editor`);
      
    } catch (err) {
      setError('Failed to download PDF');
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaFilePdf className="me-2" />
          Make PDF Editable: {documentName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant={error.includes('downloaded') ? 'info' : 'danger'}>{error}</Alert>}
        {success && (
          <Alert variant="success">
            Opening PDF editor in new tab! You can close this window.
          </Alert>
        )}

        <Alert variant="info">
          <strong>Choose how to make your PDF fillable:</strong>
        </Alert>

        <Form>
          <Form.Group className="mb-4">
            <Form.Check
              type="radio"
              id="simple"
              name="approach"
              value="simple"
              checked={approach === 'simple'}
              onChange={(e) => setApproach(e.target.value as any)}
              label={
                <div>
                  <strong>Quick Online Editor</strong>
                  <p className="text-muted mb-0">
                    Use iLovePDF's free online tool - works great for most PDFs
                  </p>
                </div>
              }
            />
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Check
              type="radio"
              id="docusign"
              name="approach"
              value="docusign"
              checked={approach === 'docusign'}
              onChange={(e) => setApproach(e.target.value as any)}
              label={
                <div>
                  <strong>Professional Editor (DocuSign)</strong>
                  <p className="text-muted mb-0">
                    Best for contracts - add signature fields, text boxes, checkboxes
                  </p>
                </div>
              }
            />
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Check
              type="radio"
              id="external"
              name="approach"
              value="external"
              checked={approach === 'external'}
              onChange={(e) => setApproach(e.target.value as any)}
              label={
                <div>
                  <strong>Adobe Acrobat Online</strong>
                  <p className="text-muted mb-0">
                    Most powerful option - handles complex PDFs and forms perfectly
                  </p>
                </div>
              }
            />
          </Form.Group>
        </Form>

        <hr />

        <Row>
          <Col>
            <h6>Alternative: Download & Edit Locally</h6>
            <p className="text-muted">
              Download the PDF and use your preferred desktop software
            </p>
            <Button
              variant="outline-secondary"
              onClick={downloadAndUploadElsewhere}
              className="w-100"
            >
              <FaDownload className="me-2" />
              Download PDF
            </Button>
          </Col>
        </Row>

        <Alert variant="warning" className="mt-3">
          <small>
            <strong>Note:</strong> For security reasons, we redirect to trusted third-party services 
            for PDF editing. Your document remains private and secure.
          </small>
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            if (approach === 'simple') handleSimpleConversion();
            else if (approach === 'docusign') handleDocuSignStyle();
            else handleExternalService();
          }}
          disabled={loading}
        >
          {loading ? 'Processing...' : (
            <>
              <FaCloudUploadAlt className="me-2" />
              Open Editor
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SimplePDFEditor;